import { Injectable } from '@angular/core';
import {
  AccountInfo,
  AuthenticationResult,
  BrowserCacheLocation,
  Configuration,
  LogLevel,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface MsalConfig {
  tenantId: string;
  clientId: string;
  cloudInstance: string;
}

@Injectable({
  providedIn: 'root',
})
export class MsalAuthService {
  private msalInstance: PublicClientApplication | null = null;
  private tokensSubject = new BehaviorSubject<{
    accessToken: string;
    idToken: string;
  } | null>(null);

  tokens$ = this.tokensSubject.asObservable();

  constructor() {
    this.checkStoredTokens();
  }

  private checkStoredTokens(): void {
    // MSAL handles token storage internally, we'll check for active account
    if (this.msalInstance) {
      const activeAccount = this.msalInstance.getActiveAccount();
      if (activeAccount) {
        this.acquireTokenSilent();
      }
    }
  }

  initializeMsal(config: MsalConfig): void {
    if (this.msalInstance) {
      return; // Already initialized
    }

    const authority = `${config.cloudInstance}${config.tenantId}`;
    const msalConfig: Configuration = {
      auth: {
        clientId: config.clientId,
        authority: authority,
        redirectUri: window.location.origin + '/',
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
        storeAuthStateInCookie: this.isIE(),
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message) => {
            console.log(message);
          },
          logLevel: LogLevel.Info,
          piiLoggingEnabled: false,
        },
      },
    };

    this.msalInstance = new PublicClientApplication(msalConfig);

    // Handle redirect promise
    this.msalInstance.handleRedirectPromise().then((response) => {
      if (response) {
        this.msalInstance!.setActiveAccount(response.account);
        this.handleAuthenticationResult(response);
      }
    });
  }

  login(): void {
    if (!this.msalInstance) {
      console.error(
        'MSAL instance not initialized. Call initializeMsal() first.',
      );
      return;
    }

    const loginRequest: RedirectRequest = {
      scopes: ['user.read'],
      redirectUri: window.location.origin + '/',
    };

    this.msalInstance.loginRedirect(loginRequest);
  }

  private acquireTokenSilent(): void {
    if (!this.msalInstance) return;

    const activeAccount = this.msalInstance.getActiveAccount();
    if (!activeAccount) return;

    const silentRequest: SilentRequest = {
      scopes: ['user.read'],
      account: activeAccount,
    };

    this.msalInstance
      .acquireTokenSilent(silentRequest)
      .then((response: AuthenticationResult) => {
        this.handleAuthenticationResult(response);
      })
      .catch((error) => {
        console.error('Silent token acquisition failed:', error);
        // Don't emit tokens if silent acquisition fails
      });
  }

  private handleAuthenticationResult(response: AuthenticationResult): void {
    const tokens = {
      accessToken: response.accessToken,
      idToken: response.idToken,
    };
    this.tokensSubject.next(tokens);
  }

  logout(): void {
    if (!this.msalInstance) return;

    const logoutRequest = {
      postLogoutRedirectUri: window.location.origin,
    };

    this.msalInstance.logoutRedirect(logoutRequest);
    this.tokensSubject.next(null);
  }

  getCurrentTokens(): { accessToken: string; idToken: string } | null {
    if (!this.msalInstance) return null;

    const activeAccount = this.msalInstance.getActiveAccount();
    if (!activeAccount) return null;

    // For current tokens, we'd need to acquire them silently
    // This is more of a check if we have an active session
    return null; // MSAL handles token storage internally
  }

  getAllAccounts(): AccountInfo[] {
    if (!this.msalInstance) return [];
    return this.msalInstance.getAllAccounts();
  }

  getActiveAccount(): AccountInfo | null {
    if (!this.msalInstance) return null;
    return this.msalInstance.getActiveAccount();
  }

  private isIE(): boolean {
    const ua = window.navigator.userAgent;
    return ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1;
  }

  // Public method to acquire token (for the higher-level service)
  acquireTokenSilentObservable(): Observable<{
    accessToken: string;
    idToken: string;
  }> {
    if (!this.msalInstance) {
      return of(null as any);
    }

    const activeAccount = this.msalInstance.getActiveAccount();
    if (!activeAccount) {
      return of(null as any);
    }

    const silentRequest: SilentRequest = {
      scopes: ['user.read'],
      account: activeAccount,
    };

    return from(this.msalInstance.acquireTokenSilent(silentRequest)).pipe(
      map((response: AuthenticationResult) => ({
        accessToken: response.accessToken,
        idToken: response.idToken,
      })),
      catchError((error) => {
        console.error('Silent token acquisition failed:', error);
        return of(null as any);
      }),
    );
  }
}
